/**
 * OneSignal web-push wiring. Call this once on app boot.
 * After the user signs in, we map their `user.id` to OneSignal `external_id`
 * so the backend can target them precisely.
 */
import OneSignal from "react-onesignal";

let initialised = false;
let initPromise = null;

export async function initOneSignal() {
    if (initialised) return;
    if (initPromise) return initPromise;
    const appId = process.env.REACT_APP_ONESIGNAL_APP_ID;
    if (!appId) {
        console.warn("OneSignal: REACT_APP_ONESIGNAL_APP_ID not set, skipping init");
        return;
    }
    initPromise = (async () => {
        try {
            await OneSignal.init({
                appId,
                allowLocalhostAsSecureOrigin: true,
                serviceWorkerPath: "/OneSignalSDKWorker.js",
                serviceWorkerParam: { scope: "/" },
                notifyButton: { enable: false },
                promptOptions: {
                    slidedown: {
                        prompts: [{
                            type: "push",
                            autoPrompt: false,
                            text: {
                                actionMessage: "Get instant updates when your order ships or your refill is due.",
                                acceptButton: "Allow",
                                cancelButton: "Not now",
                            },
                        }],
                    },
                },
            });
            initialised = true;
        } catch (e) {
            console.warn("OneSignal init failed", e);
        }
    })();
    return initPromise;
}

export async function loginOneSignal(userId) {
    if (!initialised || !userId) return;
    try {
        await OneSignal.login(String(userId));
    } catch (e) {
        console.warn("OneSignal login failed", e);
    }
}

export async function logoutOneSignal() {
    if (!initialised) return;
    try { await OneSignal.logout(); } catch { /* noop */ }
}

export async function isPushSupported() {
    try {
        return initialised && typeof OneSignal.Notifications?.permission === "string";
    } catch { return false; }
}

export async function isSubscribed() {
    try {
        if (!initialised) return false;
        return Boolean(OneSignal.User?.PushSubscription?.optedIn);
    } catch { return false; }
}

export async function promptSubscribe() {
    if (!initialised) return false;
    try {
        await OneSignal.Slidedown.promptPush();
        return await isSubscribed();
    } catch (e) {
        console.warn("promptSubscribe failed", e);
        return false;
    }
}
